import StepperDialog from 'components/modal/StepperDialog'
import React, { useEffect, useMemo, useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { ButtonMUI } from 'components/styled/Buttons'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { isNil, isUndefined, omit, mapKeys, replace, uniqBy, values } from 'lodash'
import useUpdateEffect from 'utils/customHook/useUpdateEffect'
import moment from 'moment'
import OperationAction from '../../actions/OperationAction'
import WaitAction from 'wait/WaitAction'
import { push } from '@lagunovsky/redux-react-router'
import { ANALYSIS } from '../../constants/OperationDescriptionConstants'
import { AnalysisStep, OperationStep, ParameterStep } from './Step'
import ToastrAction from 'toastr/actions/ToastrAction'
import DtoParameterCampaign from 'campaign/dto/DtoParameterCampaign'

const STEP_OPERATION = 0
const STEP_PARAMETER = 1
const STEP_ANALYSIS = 2

// convertit les paramètres d'une planification de campagne en analyses à saisir (sans doublon)
const getAnalysisFromParametersPlan = (parametersPlan = []) => uniqBy(parametersPlan.map(pP => (
    {
        ...mapKeys(omit(pP, ['campaignId', 'placeCode']), (_, key) => replace(key, 'Code', '')),
        localization: pP.placeCode,
        support: parseInt(pP.supportCode),
    }
)).map(analysis => ({ ...analysis, uniqValue: values(omit(analysis, ['frequency', 'id', 'selection'])).join('-') })), 'uniqValue')

const OperationPCStepper = ({
    isOpen = false,
    onClose = () => { },
    creationFromCampaignTracking,
    planning,
    parametersPlan,
}) => {
    const dispatch = useDispatch()

    const {
        accountUser,
        qualitometer,
        campaign,
        campaignStations,
    } = useSelector(store => ({
        accountUser: store.AccountReducer.accountUser,
        qualitometer: store.QualityReducer.qualitometer,
        campaign: store.CampaignReducer.campaign,
        campaignStations: store.CampaignReducer.campaignStations,
    }), shallowEqual)

    const defaultOperation = useMemo(() => creationFromCampaignTracking && planning ?
        ({
            date: planning.startDate,
            producer: parseInt(campaign.contributorCode),
            sampler: parseInt(campaign.samplerCode),
            labo: parseInt(planning.producer ?? campaign.recipientCode),
            support: '3', // eau
            campaign: campaign.id,
            point: campaignStations.find(({ stationId }) => stationId === qualitometer.id)?.samplePoint,
        })
        :
        ({
            date: +moment(),
            producer: accountUser.contributorCode,
            sampler: accountUser.contributorCode,
            labo: accountUser.contributorCode,
            support: '3', // eau
        })
    , [accountUser.contributorCode, planning, campaign, qualitometer, campaignStations, creationFromCampaignTracking])

    const defaultAnalysis = useMemo(() => creationFromCampaignTracking && parametersPlan ?
        getAnalysisFromParametersPlan(parametersPlan)
        :
        [], [parametersPlan, campaign])

    const [operation, setOperation] = useState(defaultOperation) // support -> to int
    const [analysis, setAnalysis] = useState(defaultAnalysis) // accreditation, trouble, localization -> to string

    useUpdateEffect(() => {
        setOperation(defaultOperation)
        setAnalysis(defaultAnalysis)
    }, [isOpen, defaultOperation])

    const filteredPoints = useMemo(() => {
        return qualitometer.link_samplePoints
            ?.filter(p => isNil(operation.support) || isNil(p.codeSupport) || `${p.codeSupport}` === operation.support)
            ?.filter(p => isNil(p.startDate) || p.startDate <= operation.date)
            ?.filter(p => isNil(p.endtDate) || p.endtDate >= operation.date) || []
    }, [operation.date, operation.support, qualitometer.link_samplePoints])

    useEffect(() => {
        if (filteredPoints.some(p => p.idPoint === operation.point)) return

        if (filteredPoints.length === 1) {
            setOperation(p => ({ ...p, point: filteredPoints[0].idPoint }))
            return
        }

        setOperation(p => ({ ...p, point: undefined }))
    }, [filteredPoints, operation.point])

    return (
        <StepperDialog
            steps={[
                {
                    label: i18n.sample,
                    constant: STEP_OPERATION,
                    nextAvailable: !isNil(operation.date) && !isNil(operation.point),
                },
                {
                    label: `${i18n.selection} / ${i18n.parameter}`,
                    constant: STEP_PARAMETER,
                    // nextAvailable: !!selectedParameters.length,
                },
                {
                    label: i18n.oneAnalysis,
                    constant: STEP_ANALYSIS,
                },
            ]}
            open={isOpen}
            title={i18n.newSample}
            closeDialog={onClose}
            renderSaveButton={step => (step === STEP_ANALYSIS) && (
                <ButtonMUI
                    onClick={() => {
                        const newOperation = {
                            id: -1,
                            qualitometer: qualitometer.id,
                            ...operation,
                            support: operation.support ? parseInt(operation.support) : undefined,
                        }
                        const newSample = {
                            id: -1,
                            qualitometer: qualitometer.id,
                            ...omit(operation, ['localization', 'comment']),
                        }
                        dispatch(WaitAction.waitStart())
                        OperationAction.createOperation(qualitometer.id, newOperation, newSample).then(({ idOperation, numSample }) => {
                            if (isUndefined(idOperation)) {
                                dispatch(WaitAction.waitStop())
                                dispatch(ToastrAction.error(i18n.createError))
                                return
                            }
                            const newAnalysis = analysis.map(a => ({
                                ...a,

                                id: -1,
                                qualitometer: qualitometer.id,
                                sample: numSample,
                                operation: idOperation,
                                analysisDate: operation.date,
                                labo: operation.labo,
                                difficulty: !isUndefined(a.difficulty) ? `${a.difficulty}` : undefined,
                                accreditation: !isUndefined(a.accreditation) ? `${a.accreditation}` : undefined,
                            }))
                            dispatch(OperationAction.createAnalysisList(qualitometer.id, idOperation, newAnalysis)).then(() => {
                                dispatch(push(`/station/quality/${qualitometer.id}/operation/${idOperation}?tab=${ANALYSIS}`))
                            }).finally(() => dispatch(WaitAction.waitStop()))
                        })
                    }}
                    variant='contained'
                >
                    {i18n.register}
                </ButtonMUI>
            )}
            contentStyle={{ padding: '0 12' }}
        >
            {
                step => (
                    <>
                        {step === STEP_OPERATION && (
                            <OperationStep
                                operation={operation}
                                setOperation={setOperation}
                                points={filteredPoints}
                            />
                        )}
                        {step === STEP_PARAMETER && (
                            <ParameterStep
                                analysis={analysis}
                                setAnalysis={setAnalysis}
                            />
                        )}
                        {step === STEP_ANALYSIS && (
                            <AnalysisStep
                                analysis={analysis}
                                setAnalysis={setAnalysis}
                            />
                        )}
                    </>
                )
            }
        </StepperDialog>
    )
}

OperationPCStepper.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    creationFromCampaignTracking: PropTypes.bool,
    planning: PropTypes.shape({
        producer: PropTypes.number,
        startDate: PropTypes.number,
    }),
    parametersPlan: PropTypes.arrayOf(DtoParameterCampaign),
}

export default OperationPCStepper
export { getAnalysisFromParametersPlan }